import React, { useEffect, useState } from 'react';
import { FiCopy, FiExternalLink, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { API_ORIGIN } from '../../../config/api';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { APPLY_FORM_QUERY, UPDATE_APPLY_FORM } from '../data/recruitmentApi';
import '../styles/AddApplicant.css';

export default function ShareApplyLink({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id: '',
    title: 'Careers Application',
    defaultRole: '',
    active: true,
    introText: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await graphqlRequest(APPLY_FORM_QUERY);
        const row = data?.recruitmentApplyForm;
        if (!cancelled && row?.id) {
          setForm({
            id: row.id,
            title: row.title || 'Careers Application',
            defaultRole: row.defaultRole || '',
            active: row.active !== false,
            introText: row.introText || '',
          });
        }
      } catch (err) {
        toast.error(err?.message || 'Could not load apply form.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyUrl = form.id
    ? `${API_ORIGIN}/embed/careers/${encodeURIComponent(form.id)}/page`
    : '';

  const copy = (text, label) =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error('Copy failed'));

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = await graphqlRequest(UPDATE_APPLY_FORM, {
        input: {
          title: form.title.trim() || 'Careers Application',
          defaultRole: form.defaultRole.trim() || null,
          active: Boolean(form.active),
          introText: form.introText.trim() || null,
        },
      });
      const row = data?.updateRecruitmentApplyForm;
      if (row?.id) {
        setForm((prev) => ({
          ...prev,
          id: row.id,
          title: row.title || prev.title,
          defaultRole: row.defaultRole || '',
          active: row.active !== false,
          introText: row.introText || '',
        }));
      }
      toast.success('Apply form settings saved.');
    } catch (err) {
      toast.error(err?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aa-overlay" onClick={onClose}>
      <div
        className="aa-modal"
        role="dialog"
        aria-labelledby="sal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aa-header">
          <div>
            <h2 id="sal-title">Share Apply Link</h2>
            <p>
              Public careers form. Submissions appear as New applicants.
            </p>
          </div>
          <button
            type="button"
            className="aa-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {loading ? (
          <p className="aa-hint">Loading…</p>
        ) : (
          <form className="aa-form" onSubmit={handleSave}>
            <label className="aa-field">
              <span>Form title</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                maxLength={120}
              />
            </label>

            <label className="aa-field">
              <span>Default role (optional)</span>
              <input
                type="text"
                value={form.defaultRole}
                onChange={(e) => setField('defaultRole', e.target.value)}
                placeholder="e.g. Cleaner"
                maxLength={120}
              />
            </label>

            <label className="aa-field">
              <span>Intro text</span>
              <textarea
                rows={3}
                value={form.introText}
                onChange={(e) => setField('introText', e.target.value)}
                maxLength={2000}
              />
            </label>

            <label className="aa-field aa-field--row">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setField('active', e.target.checked)}
              />
              <span>Form is active (accepting applications)</span>
            </label>

            <div className="aa-field">
              <span>Public apply URL</span>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'stretch',
                  flexWrap: 'wrap',
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={applyUrl}
                  style={{ flex: '1 1 220px' }}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="aa-btn aa-btn--ghost"
                  disabled={!applyUrl}
                  onClick={() => copy(applyUrl, 'Apply link')}
                >
                  <FiCopy /> Copy
                </button>
                <a
                  className="aa-btn aa-btn--ghost"
                  href={applyUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    textDecoration: 'none',
                    pointerEvents: applyUrl ? 'auto' : 'none',
                    opacity: applyUrl ? 1 : 0.5,
                  }}
                >
                  <FiExternalLink /> Open
                </a>
              </div>
            </div>

            <div className="aa-actions">
              <button
                type="button"
                className="aa-btn aa-btn--ghost"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="submit"
                className="aa-btn aa-btn--primary"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
